"use client";

import React, { useState } from 'react';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import { useTranslations } from "next-intl";
import styles from './token-refund.module.css';

export default function TokenRefundPage() {
  const params = useParams();
  const locale = params?.locale as string || 'ru';
  const isRu = locale === 'ru';
  const t = useTranslations("Legal");

  const [formData, setFormData] = useState({
    requestType: 'refund',
    fullName: '',
    email: '',
    phone: '',
    purchaseDate: '',
    tokenAmount: '',
    refundAmount: '',
    recipientEmail: '',
    recipientId: '',
    bankName: '',
    accountNumber: '',
    bik: '',
    correspondentAccount: '',
    inn: '',
    cardLast4: '',
    transactionDate: '',
    transactionAmount: '',
    hasReceipt: false,
    hasBankStatement: false,
    hasIdCopy: false,
    hasAccountScreenshot: false,
    agreedToTerms: false,
  });

  const [submitted, setSubmitted] = useState(false);
  const [requestId, setRequestId] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    setRequestId(requestId || crypto.randomUUID().split('-')[0].toUpperCase());
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className={styles.container}>
        <div className="pt-[calc(4rem+env(safe-area-inset-top))] md:pt-20"></div>
        <div className={styles.content}>
          <div className={styles.successMessage}>
            <h1>{t("refund_success_title")}</h1>
            <p>
              {isRu
                ? `Ваша заявка на ${formData.requestType === 'refund' ? 'возврат денежных средств' : 'передачу Токенов'} принята и будет рассмотрена в течение 10 рабочих дней.`
                : `Your request for ${formData.requestType === 'refund' ? 'refund' : 'token transfer'} has been received and will be reviewed within 10 business days.`
              }
            </p>
            <p>
              {isRu
                ? 'Вы получите уведомление на указанный email: '
                : 'You will receive a notification at the specified email: '
              }
              <strong>{formData.email}</strong>
            </p>
            <p className={styles.note}>
              {t("refund_success_text")}: 
              <strong>#{requestId}</strong>
            </p>
            <Link href={`/${locale}/user/dashboard`} className={styles.backButton}>
              {t("back_to_dashboard")}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className="pt-[calc(4rem+env(safe-area-inset-top))] md:pt-20"></div>
      <div className={styles.content}>
        <h1 className={styles.title}>
          {t("refund_title")}
        </h1>

        {/* Правовой блок */}
        <div className={styles.alert}>
          <h3>{isRu ? 'Важная информация о возврате (ст. 32 Закона о защите прав потребителей):' : 'Important refund information:'}</h3>

          {isRu ? (
            <>
              <p style={{ marginBottom: '0.75rem' }}>
                В соответствии со ст. 32{' '}
                <a
                  href="https://www.consultant.ru/document/cons_doc_LAW_305/"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'inherit', textDecoration: 'underline' }}
                >
                  Закона РФ № 2300-1 «О защите прав потребителей»
                </a>{' '}
                возврат денежных средств за Токены возможен при одновременном соблюдении всех условий:
              </p>
              <ul style={{ marginBottom: '0.75rem' }}>
                <li>с момента покупки прошло <strong>не более 14 календарных дней</strong>;</li>
                <li>ни один Токен из купленного пакета <strong>не был использован</strong> (0 генераций);</li>
                <li>подана настоящая заявка с полным комплектом документов.</li>
              </ul>
              <p style={{ marginBottom: '0.75rem', fontWeight: 500 }}>
                Возврат НЕ производится, если хотя бы один Токен был использован или прошло более 14 дней.
                Токены, полученные в рамках акций или компенсаций за сбой, возврату не подлежат.
              </p>
              <p style={{ marginBottom: '0.75rem' }}>
                При возврате из суммы вычитаются фактические расходы Исполнителя: комиссия платёжной системы
                (до 3,5%) и административный сбор 5% (не более 500 руб.). Возврат — тем же способом оплаты,
                срок — 10 рабочих дней.
              </p>
              <p>
                <strong>Передача Токенов</strong> другому пользователю рассматривается в течение 30 дней.
              </p>
            </>
          ) : (
            <>
              <p style={{ marginBottom: '0.75rem' }}>
                Under Art. 32 of the Russian Consumer Protection Law (№ 2300-1), a refund is possible only if:
              </p>
              <ul style={{ marginBottom: '0.75rem' }}>
                <li>No more than <strong>14 calendar days</strong> have passed since purchase;</li>
                <li><strong>Zero tokens</strong> from the purchased package have been used;</li>
                <li>This form is submitted with a full set of supporting documents.</li>
              </ul>
              <p style={{ marginBottom: '0.75rem', fontWeight: 500 }}>
                Refunds are NOT available if any tokens were used or more than 14 days have passed.
              </p>
              <p>
                Deductions apply: payment processing fee (up to 3.5%) and admin fee 5% (max 500 RUB).
                Refund via original payment method, within 10 business days.
              </p>
            </>
          )}

          <p className={styles.terms}>
            {isRu
              ? <>Полные условия в <Link href={`/${locale}/oferta`}>Публичной оферте (раздел 10)</Link></>
              : <>Full terms in <Link href={`/${locale}/oferta`}>Public Offer (section 10)</Link></>
            }
          </p>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>

          {/* Тип заявки */}
          <section className={styles.section}>
            <h2>{t("refund_form_type_label")}</h2>
            <div className={styles.radioGroup}>
              <label className={styles.radioLabel}>
                <input
                  type="radio"
                  name="requestType"
                  value="refund"
                  checked={formData.requestType === 'refund'}
                  onChange={handleChange}
                  required
                />
                <span>{t("refund_form_type_refund")}</span>
              </label>
              <label className={styles.radioLabel}>
                <input
                  type="radio"
                  name="requestType"
                  value="transfer"
                  checked={formData.requestType === 'transfer'}
                  onChange={handleChange}
                  required
                />
                <span>{t("refund_form_type_transfer")}</span>
              </label>
            </div>
          </section>

          {/* Личные данные */}
          <section className={styles.section}>
            <h2>{isRu ? 'Личные данные' : 'Personal Information'}</h2>
            <div className={styles.formGroup}>
              <label htmlFor="fullName">
                {isRu ? 'ФИО / Наименование организации *' : 'Full Name / Organization *'}
              </label>
              <input
                type="text"
                id="fullName"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                required
                placeholder={isRu ? 'Иванов Иван Иванович' : 'John Smith'}
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="email">Email *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="example@email.com"
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="phone">{isRu ? 'Телефон *' : 'Phone *'}</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                placeholder="+7 (999) 123-45-67"
              />
            </div>
          </section>

          {/* Информация о токенах */}
          <section className={styles.section}>
            <h2>{isRu ? 'Информация о Токенах' : 'Token Information'}</h2>
            <div className={styles.formGroup}>
              <label htmlFor="purchaseDate">
                {isRu ? 'Дата приобретения Токенов * (не ранее 14 дней назад)' : 'Token Purchase Date * (within last 14 days)'}
              </label>
              <input
                type="date"
                id="purchaseDate"
                name="purchaseDate"
                value={formData.purchaseDate}
                onChange={handleChange}
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="tokenAmount">
                {isRu ? 'Количество Токенов к возврату/передаче *' : 'Number of Tokens to Refund/Transfer *'}
              </label>
              <input
                type="number"
                id="tokenAmount"
                name="tokenAmount"
                value={formData.tokenAmount}
                onChange={handleChange}
                required
                min="1"
                placeholder="100"
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="refundAmount">
                {isRu ? 'Сумма оплаты за данный пакет (руб.) *' : 'Amount paid for this package (RUB) *'}
              </label>
              <input
                type="number"
                id="refundAmount"
                name="refundAmount"
                value={formData.refundAmount}
                onChange={handleChange}
                required
                min="0"
                step="0.01"
                placeholder="1000.00"
              />
            </div>
          </section>

          {/* Данные получателя (при передаче) */}
          {formData.requestType === 'transfer' && (
            <section className={styles.section}>
              <h2>{isRu ? 'Данные получателя Токенов' : 'Recipient Information'}</h2>
              <div className={styles.formGroup}>
                <label htmlFor="recipientEmail">
                  {isRu ? 'Email получателя *' : 'Recipient Email *'}
                </label>
                <input
                  type="email"
                  id="recipientEmail"
                  name="recipientEmail"
                  value={formData.recipientEmail}
                  onChange={handleChange}
                  required={formData.requestType === 'transfer'}
                  placeholder="recipient@email.com"
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="recipientId">
                  {isRu ? 'ID аккаунта получателя (если известен)' : 'Recipient Account ID (if known)'}
                </label>
                <input
                  type="text"
                  id="recipientId"
                  name="recipientId"
                  value={formData.recipientId}
                  onChange={handleChange}
                  placeholder="USER123456"
                />
              </div>
            </section>
          )}

          {/* Банковские реквизиты (при возврате) */}
          {formData.requestType === 'refund' && (
            <section className={styles.section}>
              <h2>{isRu ? 'Банковские реквизиты для возврата' : 'Bank Details for Refund'}</h2>
              <p className={styles.note}>
                {isRu
                  ? 'Возврат осуществляется тем же способом, которым была произведена оплата. Реквизиты должны совпадать с реквизитами плательщика.'
                  : 'Refunds are made via the same payment method. Details must match the original payer.'
                }
              </p>

              <div className={styles.formGroup}>
                <label htmlFor="bankName">{isRu ? 'Наименование банка *' : 'Bank Name *'}</label>
                <input
                  type="text"
                  id="bankName"
                  name="bankName"
                  value={formData.bankName}
                  onChange={handleChange}
                  required={formData.requestType === 'refund'}
                  placeholder={isRu ? 'ПАО Сбербанк' : 'Bank Name'}
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="accountNumber">{isRu ? 'Номер счёта *' : 'Account Number *'}</label>
                <input
                  type="text"
                  id="accountNumber"
                  name="accountNumber"
                  value={formData.accountNumber}
                  onChange={handleChange}
                  required={formData.requestType === 'refund'}
                  placeholder="40817810000000000000"
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="bik">{isRu ? 'БИК банка *' : 'Bank BIC *'}</label>
                <input
                  type="text"
                  id="bik"
                  name="bik"
                  value={formData.bik}
                  onChange={handleChange}
                  required={formData.requestType === 'refund'}
                  placeholder="044525225"
                  maxLength={9}
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="correspondentAccount">{isRu ? 'Корреспондентский счёт' : 'Correspondent Account'}</label>
                <input
                  type="text"
                  id="correspondentAccount"
                  name="correspondentAccount"
                  value={formData.correspondentAccount}
                  onChange={handleChange}
                  placeholder="30101810400000000225"
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="inn">{isRu ? 'ИНН (для юр. лиц)' : 'Tax ID (for legal entities)'}</label>
                <input
                  type="text"
                  id="inn"
                  name="inn"
                  value={formData.inn}
                  onChange={handleChange}
                  placeholder="7700000000"
                />
              </div>
            </section>
          )}

          {/* Данные платежа */}
          <section className={styles.section}>
            <h2>{isRu ? 'Данные платежа' : 'Payment Details'}</h2>
            <div className={styles.formGroup}>
              <label htmlFor="cardLast4">{isRu ? 'Последние 4 цифры карты *' : 'Last 4 Digits of Card *'}</label>
              <input
                type="text"
                id="cardLast4"
                name="cardLast4"
                value={formData.cardLast4}
                onChange={handleChange}
                required
                placeholder="1234"
                maxLength={4}
                pattern="[0-9]{4}"
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="transactionDate">{isRu ? 'Дата и время транзакции *' : 'Transaction Date and Time *'}</label>
              <input
                type="datetime-local"
                id="transactionDate"
                name="transactionDate"
                value={formData.transactionDate}
                onChange={handleChange}
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="transactionAmount">{isRu ? 'Сумма транзакции (руб.) *' : 'Transaction Amount (RUB) *'}</label>
              <input
                type="number"
                id="transactionAmount"
                name="transactionAmount"
                value={formData.transactionAmount}
                onChange={handleChange}
                required
                min="0"
                step="0.01"
                placeholder="1000.00"
              />
            </div>
          </section>

          {/* Подтверждающие документы */}
          <section className={styles.section}>
            <h2>{isRu ? 'Подтверждающие документы' : 'Supporting Documents'}</h2>
            <p className={styles.note}>
              {isRu
                ? <>Отметьте документы, которые вы готовы предоставить. Все документы направьте на{' '}
                    <a href="mailto:support@kinofolio.com" style={{ color: 'inherit', textDecoration: 'underline' }}>
                      support@kinofolio.com
                    </a>{' '}
                    с темой &ldquo;Заявка на возврат #{'{номер заявки}'}&rdquo;</>
                : <>Check documents you will provide. Send all to{' '}
                    <a href="mailto:support@kinofolio.com" style={{ color: 'inherit', textDecoration: 'underline' }}>
                      support@kinofolio.com
                    </a>{' '}
                    with subject &ldquo;Refund Request #{'{request number}'}&rdquo;</>
              }
            </p>

            <div className={styles.checkboxGroup}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  name="hasReceipt"
                  checked={formData.hasReceipt}
                  onChange={handleChange}
                  required
                />
                <span>
                  {isRu
                    ? 'Кассовый чек (электронный или бумажный) о приобретении Токенов * — выдаётся согласно ФЗ № 54-ФЗ'
                    : 'Payment receipt of token purchase * (required by Russian law № 54-FZ)'
                  }
                </span>
              </label>

              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  name="hasBankStatement"
                  checked={formData.hasBankStatement}
                  onChange={handleChange}
                  required
                />
                <span>
                  {isRu
                    ? 'Выписка из банка, подтверждающая совершение платежа *'
                    : 'Bank statement confirming the payment *'
                  }
                </span>
              </label>

              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  name="hasIdCopy"
                  checked={formData.hasIdCopy}
                  onChange={handleChange}
                  required
                />
                <span>
                  {isRu
                    ? 'Копия документа, удостоверяющего личность (для физ. лиц) * — согласно ФЗ № 115-ФЗ «О противодействии легализации доходов»'
                    : 'Copy of ID document (for individuals) *'
                  }
                </span>
              </label>

              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  name="hasAccountScreenshot"
                  checked={formData.hasAccountScreenshot}
                  onChange={handleChange}
                  required
                />
                <span>
                  {isRu
                    ? 'Скриншот личного кабинета с балансом Токенов (с нулём использованных) *'
                    : 'Screenshot of account dashboard showing token balance (zero used) *'
                  }
                </span>
              </label>
            </div>
          </section>

          {/* Согласие */}
          <section className={styles.section}>
            <div className={styles.termsSection}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  name="agreedToTerms"
                  checked={formData.agreedToTerms}
                  onChange={handleChange}
                  required
                />
                <span>
                  {isRu
                    ? <>
                        Я подтверждаю, что ознакомлен(а) с условиями{' '}
                        <Link href={`/${locale}/oferta`} target="_blank">Публичной оферты (раздел 10)</Link>,
                        предоставляю достоверную информацию и согласен(на) на обработку персональных данных
                        в соответствии с{' '}
                        <a
                          href="https://www.consultant.ru/document/cons_doc_LAW_61801/"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          ФЗ № 152-ФЗ «О персональных данных»
                        </a>{' '}
                        и{' '}
                        <Link href={`/${locale}/privacy`} target="_blank">Политикой конфиденциальности</Link>.
                        Подтверждаю, что на момент подачи заявки ни один Токен из заявленного пакета не был использован. *
                      </>
                    : <>
                        I confirm I have read the{' '}
                        <Link href={`/${locale}/oferta`} target="_blank">Public Offer (section 10)</Link>,
                        provide accurate information, consent to personal data processing under
                        Russian Federal Law № 152-FZ, and confirm that zero tokens from the
                        stated package have been used. *
                      </>
                  }
                </span>
              </label>
            </div>
          </section>

          <div className={styles.submitSection}>
            <button type="submit" className={styles.submitButton}>
              {isRu ? 'Отправить заявление' : 'Submit Request'}
            </button>
            <p className={styles.note}>
              {isRu
                ? 'Заявление на возврат рассматривается в течение 10 рабочих дней с момента получения полного комплекта документов (согласно ст. 22 ЗПП). Передача токенов — в течение 30 дней.'
                : 'Refund requests are reviewed within 10 business days of receiving all documents. Token transfers within 30 days.'
              }
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}
